/**
 * Representative photography — Unsplash License and Pexels License (both free for commercial use,
 * no attribution required; sources and photographers are listed in public/images/photos/CREDITS.md).
 *
 * These are illustrative industry images, NOT photographs of Shawal's own facilities, staff or products.
 * Alt texts therefore describe what is shown, never "our factory". Replace them with genuine company
 * photography when available: add files to /public/images/photos and update this registry.
 * Product photos are managed per product in the admin portal.
 */
export interface PhotoAsset {
  /** Base file name in /public/images/photos (renditions are `${file}-${width}.webp`). */
  file: string;
  widths: number[];
  /** Intrinsic aspect ratio of the source image. */
  width: number;
  height: number;
  alt: string;
  /** Photo page on the source library (provenance). */
  source: string;
  /** Default CSS object-position for crops (focal point), used when a page doesn't set one. */
  position?: string;
}

const unsplash = (id: string) => `https://unsplash.com/photos/${id}`;
const pexels = (id: string) => `https://www.pexels.com/photo/${id}/`;

const photo = (file: string, widths: number[], width: number, height: number, alt: string, source: string): PhotoAsset => ({
  file,
  widths,
  width,
  height,
  alt,
  source,
});

export const PHOTOS = {
  tailorMachine: photo('homepage-hero-factory', [1440, 1942], 1942, 809, 'Illustrative image of male workers stitching sportswear on industrial sewing machines in a garment factory', 'supplied by the site owner (illustrative image)'),
  threadCones: photo('overlock-thread-cones', [320, 480, 800], 2400, 3593, 'Orange thread cones feeding an industrial overlock sewing machine', pexels('31591460')),
  garmentWorkshop: {
    ...photo('pakistani-garment-workshop', [1440, 1790], 1790, 879, 'Illustrative image of male garment workers producing sportswear in a Pakistani garment workshop.', 'supplied by the site owner (illustrative image)'),
    position: '66% 50%',
  },
  machineThreading: photo('textile-machine-threading', [480, 800, 1200], 2400, 1600, 'Operator threading yarn through a textile machine in a factory', pexels('39351210')),
  cartonPacking: photo('carton-packing-room', [480, 800, 1200], 2400, 1600, 'Workers packing goods into cardboard cartons in a packing room', pexels('39351270')),
  ballStitching: photo('cricket-ball-hand-stitching', [640, 960, 1440], 2400, 3600, 'Craftsman hand-stitching the seam of a leather cricket ball', pexels('20269342')),
  cricketBallHand: photo('cricket-ball-in-hand', [480, 800, 1200], 2400, 3600, 'Hand holding a red leather cricket ball with a raised stitched seam', unsplash('1Ls6HxVr5fM')),
  footballGrass: photo('football-on-grass', [480, 800, 1200], 2400, 1602, 'Classic black-and-white football resting on grass', unsplash('OCH9nlz0BY4')),
  blankTag: photo('blank-garment-tag', [480, 800, 1200], 2400, 3200, 'Blank hang tag attached to a garment, ready for custom branding', unsplash('J_UuEa_aIeI')),
  embroidery: photo('industrial-embroidery', [480, 800, 1200, 1600], 2400, 1600, 'Multi-head industrial embroidery machines stitching a design onto dark fabric', pexels('32641546')),
  foldedTees: photo('folded-tshirts', [480, 800, 1200], 2400, 4266, 'Plain t-shirts folded in a grid of colours', unsplash('tWOz2_EK5EQ')),
  hoodies: photo('hoodies', [480, 800, 1200], 2400, 1600, 'Grey and black hoodies with drawstrings', unsplash('dqjGsWq2cII')),
  streetwearTees: photo('streetwear-tees', [480, 800, 1200], 2400, 4266, 'Black and mint t-shirts laid flat with cotton bolls', unsplash('EFCpLBQbxIU')),
  windingLine: photo('textile-winding-line', [640, 960, 1440], 2400, 1600, 'Factory worker standing beside a textile winding line', pexels('39351224')),
  cricketBallSeam: photo('cricket-ball-seam', [640, 960, 1440], 2400, 1607, 'Red leather cricket ball with a stitched seam lying in grass', unsplash('oDs_AxeR5g4')),
  fabricTexture: photo('dark-fabric-texture', [960], 2400, 1800, '', unsplash('Js5Qi_X1qzo')),
} satisfies Record<string, PhotoAsset>;

export type PhotoName = keyof typeof PHOTOS;

export function photoSrc(p: PhotoAsset, width?: number) {
  const w = width ?? p.widths[p.widths.length - 1]!;
  return `/images/photos/${p.file}-${w}.webp`;
}

export function photoSrcSet(p: PhotoAsset) {
  return p.widths.map((w) => `${photoSrc(p, w)} ${w}w`).join(', ');
}

/** Category slug → representative photo (categories without one get a typographic tile). */
export const CATEGORY_PHOTOS: Record<string, PhotoName> = {
  't-shirts': 'foldedTees',
  'hoodies-sweatshirts': 'hoodies',
  streetwear: 'streetwearTees',
  footballs: 'footballGrass',
  'cricket-balls': 'cricketBallSeam',
};
